import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI */
import { Box, Tabs, Tab, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';

import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch } from 'react-redux';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import { MESSAGES } from '../../lib/configUtils';
/** Internal Components */
import { tabsRootSx } from 'shared/config/Styles';
import { getEntryFeeColumns, getDutyTaxColumns, getDutyRefundColumns, getTruckingFeeColumns } from './JinroERPDef';
import { dutyEntryDetailGridSx } from 'shared/config/Styles';
import ThemeButton from 'shared/ui/Button';
import { callAPI } from 'shared/api/callApi';

const COLUMN_GETTERS = {
    ENTRY: [getEntryFeeColumns, getDutyTaxColumns, getDutyRefundColumns],
    TRUCKING: [getTruckingFeeColumns],
};

const TAB_LABELS_ENTRY = ['Entry Fee', 'Duty Tax', 'Duty Refund'];
const TAB_LABELS_TRUCKING = ['Trucking Fee'];

const VALIDITY_KEYS_BY_TAB = {
    ENTRY: ['entry_validity', 'duty_validity', 'refund_validity'],
    TRUCKING: ['trucking_validity'],
};

export default function JinroERPDetail({
    row,
    userEmail,
    getAccessTokenSilently,
    jinroSummariesRows,
    setJinroSummariesRows,
    columnsType,
    setLoadingState,
}) {
    const dispatch = useDispatch();

    const [tabValue, setTabValue] = useState(0);
    const [summaryRows, setSummaryRows] = useState([]);
    console.log('row', row);
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleDeleteClick = () => {
        const isConfirmed = window.confirm('Delete this jinro summary? This action cannot be undone.');
        if (!isConfirmed) {
            return;
        }
        const ids = row.il_jinro_summary.map((item) => item.id);

        callAPI({
            method: 'delete',
            module: 'duty_calculator',
            component: 'jinro_summary',
            getAccessTokenSilently,
            email: userEmail,
            obj: { id: ids },
            onInit: () => {
                setLoadingState((prev) => ({ ...prev, circularLoading: true }));
            },
            onFinal: () => {
                setLoadingState((prev) => ({ ...prev, circularLoading: false }));
            },
            onSuccess: (resp) => {
                console.log('delete jinro summary response', resp);
                setSummaryRows([]);
                setJinroSummariesRows(jinroSummariesRows.filter((item) => item.id !== row.id));
                dispatch(setSnackbar({ open: true, message: MESSAGES.DELETE_SUCCESS, severity: 'success' }));
            },
            onError: (error) => {
                console.log('delete jinro summary error', error);
            },
        });
    };

    useEffect(() => {
        const summaryRows = row.il_jinro_summary ?? [];
        setSummaryRows(summaryRows);
    }, [row]);
    console.log('summaryRows', summaryRows);
    const REFUND_FEE_ROW_ID = '__refund_fee_line__';
    const displayRows =
        columnsType === 'ENTRY' && tabValue === 2
            ? [
                  {
                      id: REFUND_FEE_ROW_ID,
                      duty_refund_debit_amount: row.refund_fee,
                      item_code: row.contract_number,
                  },
                  ...(summaryRows || []),
              ]
            : summaryRows || [];
    return (
        <Box sx={{ bgcolor: '#f1f1f1' }}>
            <Box
                sx={{
                    width: '70%',
                    mx: 'auto',
                    p: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 0,
                        px: 0,
                        py: 0,
                        borderBottom: 'none',
                    }}
                >
                    <Tabs value={tabValue} onChange={handleTabChange} sx={tabsRootSx} scrollButtons={false}>
                        {columnsType === 'ENTRY'
                            ? TAB_LABELS_ENTRY?.map((tab, index) => <Tab key={index} value={index} label={tab} />)
                            : TAB_LABELS_TRUCKING?.map((tab, index) => <Tab key={index} value={index} label={tab} />)}
                    </Tabs>
                    <Box sx={{ display: 'flex', alignItems: 'end', gap: 1 }}>
                        {(() => {
                            const validityKey = VALIDITY_KEYS_BY_TAB[columnsType]?.[tabValue] ?? 'entry_validity';
                            const isValid = row[validityKey] === true;
                            return (
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: isValid ? '#58A000' : '#d92936',
                                    }}
                                >
                                    {isValid ? 'VALID' : 'INVALID'}
                                </Typography>
                            );
                        })()}
                        <ThemeButton
                            text="Delete"
                            icon={<DeleteIcon />}
                            onClick={handleDeleteClick}
                            sx={{ width: '80px' }}
                        />
                    </Box>
                </Box>

                {/* 그리드 박스안에 들어가는거 간격 맞춤 */}
                <Box sx={{ px: 0, py: 0, bgcolor: '#ffffff' }}>
                    <DataGridPremium
                        rows={displayRows || []}
                        columns={
                            COLUMN_GETTERS[columnsType]?.[tabValue]?.() ?? COLUMN_GETTERS[columnsType]?.[0]?.() ?? []
                        }
                        // density="compact"
                        onProcessRowUpdateError={(error) => console.error('Error processing row update:', error)}
                        hideFooter
                        disableColumnMenu
                        disableColumnSorting
                        cellSelection
                        disableRowSelectionOnClick
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    id: false,
                                },
                            },
                        }}
                        rowHeight={25}
                        columnHeaderHeight={27}
                        sx={{
                            ...dutyEntryDetailGridSx,
                            '& .MuiDataGrid-cell': {
                                border: '1px solid #e0e0e0',
                            },
                            '& .MuiDataGrid-cell.jinro-entry-amount-rounding-adjusted': {
                                backgroundColor: '#fff9c4',
                            },
                            '& .MuiDataGrid-cell.jinro-trucking-amount-rounding-adjusted': {
                                backgroundColor: '#fff9c4',
                            },
                            '& .MuiDataGrid-cell.jinro-refund-credit-amount-rounding-adjusted': {
                                backgroundColor: '#fff9c4',
                            },
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
}
